import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild }               from '@angular/core';
import { DecimalPipe }      from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import { FileUploadOptionsModel } from './typings/file-upload.options';
import { FileUploadService }      from './file-upload.service';
import { MessagesService }        from '../../../messages/messages.service';

@Component({
  selector   : 'wor-file-upload-control',
  template: require('./file-upload-control.component.html')
})
export class FileUploadControlComponent implements OnInit, OnDestroy {
  @Input() label    ?: string;
  @Input() labelNote?: string;
  @Input() options   : FileUploadOptionsModel;

  @Output() uploadedPreview = new EventEmitter<string>();

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  acceptedTypes    : string;
  boundOnReaderLoad: any;
  file             : File;
  preview          : string;
  reader           : FileReader;

  uploadReady  = false;
  uploading    = false;

  imageExtensions = [
    'gif',
    'jpeg',
    'jpg',
    'png'
  ];

  constructor (
    private decimalPipe      : DecimalPipe,
    private fileUploadService: FileUploadService,
    private router           : UIRouter,
    private messagesService  : MessagesService,
    private translateService : TranslateService
  ) {}

  getAcceptedTypes () : string {
    switch ( this.options?.accept ) {
      case 'images':
      default:
        return '.gif,.jpeg,.jpg,.png';
    }
  }

  getExtension ( filename : string ) : string {
    return this.fileUploadService.getExtension(filename);
  }

  ngOnDestroy () : void {
    if ( this.reader && this.boundOnReaderLoad ) {
      this.reader.removeEventListener('load', this.boundOnReaderLoad);
    }

    this.fileUploadService.clearRequest();
  }

  ngOnInit () : void {
    this.acceptedTypes = this.getAcceptedTypes();
    this.preview       = this.options?.file;
  }

  onFileChange ( event : Event ) : void {
    const input = event.target as HTMLInputElement;
    const file  = input?.files?.[ 0 ];

    if ( !file ) {
      return;
    }

    if ( file.size > this.options.size ) {
      if ( this.fileInput?.nativeElement ) {
        this.fileInput.nativeElement.value = '';
      }

      this.messagesService.show(`${ this.options.type }'s cannot exceed ${ this.prettifySize() }`, 'error');

      return;
    }

    if ( this.options.accept === 'images' && this.imageExtensions.indexOf(this.getExtension(file.name)) < 0 ) {
      if ( this.fileInput?.nativeElement ) {
        this.fileInput.nativeElement.value = '';
      }

      const allowed = this.imageExtensions.map(x => x.toUpperCase()).join(', ');

      this.messagesService.show(`File must be one of the following types: ${ allowed }`, 'error');

      return;
    }

    this.file   = file;
    this.reader = new FileReader();

    this.boundOnReaderLoad = this.onReaderLoad.bind(this);

    this.reader.addEventListener('load', this.boundOnReaderLoad);

    this.uploadReady = true;

    this.reader.readAsDataURL(file);
  }

  onReaderLoad ( e : any ) : void {
    this.preview = e?.target?.result;
  }

  prettifySize () : string {
    const mb = (this.options?.size || 0) / 1000000;

    if ( mb < 1 ) {
      const kb = mb * 1000;

      return `${ this.decimalPipe.transform(kb, '1.0-0') }KB`;
    }

    return `${ this.decimalPipe.transform(mb, '1.0-2') }MB`;
  }

  showPreview () : boolean {
    return !!this.preview
      && !this.options?.hidePreview;
  }

  upload () : void {
    if ( !this.file ) {
      return;
    }

    this.uploading = true;

    this.fileUploadService.upload(this.file, this.options)
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.UPLOAD_SUCCESSFUL'), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => {
      this.uploading = false;

      // Reset input value so same file can be reselected if needed
      if ( this.fileInput?.nativeElement ) {
        this.fileInput.nativeElement.value = '';
      }
    });
  }
}
